/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import MsDyn365, {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    ICreateActionContext
} from '@msdyn365-commerce/core';
import {
    AsyncResult,
    ChannelInventoryConfiguration,
    DeliveryOption,
    ProductInventoryAvailability,
    ProductSearchCriteria,
    ProductSearchResult
} from '@msdyn365-commerce/retail-proxy';

import { getEstimatedAvailabilityAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { getDeliveryOptionsAsync, searchByCriteriaAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { getInventoryConfigurationAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/StoreOperationsDataActions.g';
import {
    ArrayExtensions,
    generateProductImageUrl,
    getCurrentCategoryHierarchy,
    InventoryLevels,
    IProductInventoryInformation,
    ObjectExtensions
} from '@msdyn365-commerce-modules/retail-actions';
import { CategoryHierarchy } from '@msdyn365-commerce/commerce-entities';
import { CurrentCategoryHierarchyInput } from '../../../themes/dobbies/actions/get-current-category-hierarchy.override.action';
import { ListPageStateInput } from '../list-page-state';
import { BaseCollectionInput, createBaseCollectionInput } from './base-collection-action';
import { mapProductInventoryInformation } from '../../../themes/dobbies/utilities/inventory/product-inventory-utils';
import { CategoriesInput, getCategoryHierarchyAction } from '../../../themes/dobbies/actions/get-categories-hierarchy.override.action';

type DeviceType = 'Mobile';
export const RESTORE_CHUNK_SIZE = 60;

/**
 * GetFullProductsByCollection Action Input.
 */
export class GetFullProductsByCollectionInput extends BaseCollectionInput implements IActionInput {
    /**
     * The cache object type.
     * @returns The cache object type.
     */
    public getCacheObjectType = (): string => 'FullProductSearchResult';

    /**
     * The data cache type.
     * @returns The data cache type.
     */
    public dataCacheType = (): CacheType => {
        if (
            this.pageType !== 'Category' ||
            (this.refiners && this.refiners.length > 0) ||
            (this.queryResultSettings &&
                this.queryResultSettings.Sorting &&
                this.queryResultSettings.Sorting.Columns &&
                this.queryResultSettings.Sorting.Columns.length > 0)
        ) {
            return 'none';
        }
        // Needed to be done because when URL with uc_param is hit, it just returns old Cached result, so we need to
        // invalidate cache every time.
        return 'none';
    };
}

/**
 * This setting defines inventory filtering options.
 */
export enum ProductListInventoryFilteringOptions {
    /**
     * Filter out all products out of stock.
     */
    HideOOS = 'hideOOS',

    /**
     * Sort products by availability, OOS goes last.
     */
    SortOOS = 'sortOOS',

    /**
     * No filtering selected.
     */
    Default = 'default'
}

export interface ProductSearchResultExtended extends ProductSearchResult {
    DeliveryOptions?: DeliveryOption[];
    ProductHomeDeliveryInventory?: IProductInventoryInformation;
    ProductClickAndCollectInventory?: ProductInventoryAvailability;
}

/**
 * The full product search result with count interface.
 */
export interface IFullProductsSearchResultsWithCount {
    products: ProductSearchResultExtended[];
    count?: number;
    channelInventoryConfigurationId?: number;
}

/**
 * CreateInput function which creates and actionInput used to fetch products for a list page.
 * @param args - The API arguments.
 * @returns IActionInput - The action input.
 */
const createInput = (args: ICreateActionContext<{ itemsPerPage: number; includedAttributes: boolean | undefined }>): IActionInput => {
    const input = createBaseCollectionInput(args, GetFullProductsByCollectionInput);

    // Set Top
    if (input.queryResultSettings.Paging) {
        const isMobile = args.requestContext.device && (args.requestContext.device.Type as DeviceType) === 'Mobile';
        let top = args.requestContext.app.config.itemsPerPage || 60;
        if (isMobile) {
            top = args.requestContext.app.config.itemsPerPageMobile || 20;
        }
        input.queryResultSettings.Paging.Top = top;
    }

    // Set Skip
    if (input.queryResultSettings.Paging && args.requestContext.query && args.requestContext.query.skip) {
        // const skip = +args.requestContext.query.skip;
        // if (!isNaN(skip) && skip > 0) {
        //     input.queryResultSettings.Paging.Top = Math.min(skip, RESTORE_CHUNK_SIZE);
        //     input.queryResultSettings.Paging.Skip = 0;
        // }
        input.queryResultSettings.Paging.Skip = +args.requestContext.query.skip;
    }

    input.queryResultSettings.count = true;

    return input;
};

/**
 * Finds whether a product is out of stock based on the attribute.
 * @param  product - The product.
 * @param  channelInventoryConfiguration - The channel configuration.
 * @returns True if is OOS, false otherwise.
 */
function isOutOfStock(product: ProductSearchResultExtended, channelInventoryConfiguration: ChannelInventoryConfiguration): boolean {
    if (!ArrayExtensions.hasElements(product.AttributeValues)) {
        // If no attributes then assume is in stock
        return false;
    }
    for (const attribute of product.AttributeValues) {
        if (
            attribute.RecordId === channelInventoryConfiguration.InventoryProductAttributeRecordId &&
            attribute.TextValue === channelInventoryConfiguration.InventoryOutOfStockAttributeValueText
        ) {
            // The matching recordId indicates that is the inventory attribute
            // if the text value are the same then is out of stock.
            return true;
        }
    }

    return false;
}

/**
 * Returns only in stock products.
 * @param  products - The product.
 * @param  channelInventoryConfiguration - The channel configuration.
 * @returns Filtered product search result.
 */
function filterOutOfStockProducts(
    products: ProductSearchResultExtended[],
    channelInventoryConfiguration: ChannelInventoryConfiguration
): ProductSearchResultExtended[] {
    if (!ArrayExtensions.hasElements(products)) {
        return [];
    }
    const filteredProducts: ProductSearchResultExtended[] = [];

    for (const product of products) {
        if (!isOutOfStock(product, channelInventoryConfiguration)) {
            filteredProducts.push(product);
        }
    }
    return filteredProducts;
}

/**
 * Returns sorted products based on availability.
 * @param  products - The product.
 * @param  channelInventoryConfiguration - The channel configuration.
 * @returns Sorted product search result.
 */
function sortsProductsBasedOnAvailability(
    products: ProductSearchResultExtended[],
    channelInventoryConfiguration: ChannelInventoryConfiguration
): ProductSearchResultExtended[] {
    if (!ArrayExtensions.hasElements(products)) {
        return [];
    }
    const inStockProducts: ProductSearchResultExtended[] = [];
    const outOfStockProducts: ProductSearchResultExtended[] = [];

    for (const product of products) {
        if (isOutOfStock(product, channelInventoryConfiguration)) {
            outOfStockProducts.push(product);
        } else {
            inStockProducts.push(product);
        }
    }
    return inStockProducts.concat(outOfStockProducts);
}

/**
 * Returns list of products based on inventory information.
 * @param  products - The products.
 * @param  channelInventoryConfiguration - The channel configuration.
 * @param  context - The context.
 * @returns List of product based on the inventory information.
 */
export function returnProductsBasedOnInventoryInformation(
    products: ProductSearchResultExtended[],
    channelInventoryConfiguration: ChannelInventoryConfiguration,
    context: IActionContext
): ProductSearchResultExtended[] {
    if (!ArrayExtensions.hasElements(products) || ObjectExtensions.isNullOrUndefined(channelInventoryConfiguration)) {
        return [];
    }
    switch (context.requestContext.app.config.productListInventoryDisplay) {
        case ProductListInventoryFilteringOptions.HideOOS:
            return filterOutOfStockProducts(products, channelInventoryConfiguration);
        case ProductListInventoryFilteringOptions.SortOOS:
            return sortsProductsBasedOnAvailability(products, channelInventoryConfiguration);
        default:
            return products;
    }
}

/**
 * Returns list of products based on inventory information.
 * @param  productSearchResults - The products.
 * @param  context - The context.
 * @param  metadataCount - The metadata count.
 * @returns List of product based on the inventory information.
 */
export async function returnProducts(
    productSearchResults: ProductSearchResultExtended[],
    context: IActionContext,
    metadataCount: number | undefined
): Promise<IFullProductsSearchResultsWithCount> {
    const productSearchResultsWithImages = productSearchResults.map(productSearchResult => {
        const newImageUrl = generateProductImageUrl(productSearchResult, context.requestContext.apiSettings);

        if (newImageUrl) {
            productSearchResult.PrimaryImageUrl = newImageUrl;
        }

        return productSearchResult;
    });

    // If inventory level is threshold or inventory check is disabled then return the list of products without the inventory configuration
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- read config file.
    if (
        context.requestContext.app.config.inventoryLevel === InventoryLevels.threshold ||
        context.requestContext.app.config.enableStockCheck === false
    ) {
        return {
            products: productSearchResultsWithImages,
            count: metadataCount
        };
    }
    const channelInventoryConfiguration = await getInventoryConfigurationAsync({ callerContext: context });

    const mappedProducts = productSearchResultsWithImages.map(productSearchResult => {
        if (ArrayExtensions.hasElements(productSearchResult.AttributeValues)) {
            for (const element of productSearchResult.AttributeValues) {
                // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- need check.
                if (
                    channelInventoryConfiguration &&
                    element.RecordId !== undefined &&
                    element.RecordId === channelInventoryConfiguration.InventoryProductAttributeRecordId &&
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- read config file.
                    context.requestContext.app.config.inventoryRanges !== 'all' &&
                    element.TextValue !== channelInventoryConfiguration.InventoryOutOfStockAttributeValueText
                ) {
                    // If same RecordId then it means that is the Inventory attribute
                    // Based on the inventory range (and filtering options), the inventory label will be displayed
                    // If Inventory range is 'All' then in stock and out of stock labels are shown, else only OOS
                    // if the text value is different that the channelInventoryConfiguration.InventoryOutOfStockAttributeValueText then is in stock
                    element.TextValue = '';
                }
            }
        }

        return productSearchResult;
    });

    const productsWithInventory = await getProductsInventory(mappedProducts, context);
    const productsWithDeliveryOptions = await getProductsDeliveryOptions(productsWithInventory || mappedProducts, context);

    return {
        products: productsWithDeliveryOptions || mappedProducts,
        count: metadataCount,
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- check config.
        channelInventoryConfigurationId: channelInventoryConfiguration
            ? channelInventoryConfiguration.InventoryProductAttributeRecordId
            : undefined
    };
}

async function getProductsDeliveryOptions(
    products: ProductSearchResultExtended[],
    context: IActionContext
): Promise<ProductSearchResultExtended[] | undefined> {
    // Implementation for fetching delivery options for each product
    try {
        let productsWithDeliveryOptions: ProductSearchResultExtended[] = [...products];
        const productIds = products.map(product => product.RecordId);
        const deliveryOptions = await getDeliveryOptionsAsync({ callerContext: context, queryResultSettings: {} }, productIds);
        productsWithDeliveryOptions = productsWithDeliveryOptions.map(product => {
            const deliveryOption = deliveryOptions.find(option => option.ProductId === product.RecordId);
            return {
                ...product,
                DeliveryOptions: deliveryOption?.DeliveryOptions || []
            };
        });
        return productsWithDeliveryOptions;
    } catch (error) {
        context.telemetry.error('Error fetching delivery options', error);
        return products; // Return original products if there's an error
    }
}

async function getProductsInventory(
    products: ProductSearchResultExtended[],
    context: IActionContext
): Promise<ProductSearchResultExtended[] | undefined> {
    try {
        const productsWithVariantIds = [...products];
        for (const product of productsWithVariantIds) {
            const variantId = product.ExtensionProperties?.find(prop => prop.Key === 'VariantId')?.Value?.LongValue;
            // If the product is a master product and has a variant ID (Kit Products)
            if (product.IsMasterProduct && variantId) {
                product.RecordId = variantId || product.RecordId;
            }
        }
        const productIds = productsWithVariantIds.map(product => product.RecordId);
        // Fetch Home Delivery Inventory (default warehouse)
        const homeDeliveryInventory = await getEstimatedAvailabilityAsync(
            { callerContext: context },
            {
                ProductIds: productIds,
                DefaultWarehouseOnly: true,
                FilterByChannelFulfillmentGroup: false
            }
        );
        let productsWithInventory: ProductSearchResultExtended[] = [...productsWithVariantIds];
        const mappedProductInventory = mapProductInventoryInformation(
            context,
            homeDeliveryInventory.ProductWarehouseInventoryAvailabilities || []
        );

        productsWithInventory = productsWithInventory.map(product => {
            const productInventory = mappedProductInventory.find(
                inventory => inventory.ProductAvailableQuantity.ProductId === product.RecordId
            );
            const isVendorShipAttribute = product.AttributeValues?.find(attribute => {
                const attributeName = attribute.Name?.trim()?.toLowerCase();
                return attributeName === 'isvendershipproduct';
            });
            // Return static dummy inventory if product is a vendor product
            if (isVendorShipAttribute?.TextValue?.toLowerCase() === 'yes') {
                const vendorProductInventory: IProductInventoryInformation = {
                    ProductAvailableQuantity: {
                        ProductId: product.RecordId,
                        AvailableQuantity: 9999
                    },
                    IsProductAvailable: true
                };
                return {
                    ...product,
                    ProductHomeDeliveryInventory: vendorProductInventory
                };
            }

            return {
                ...product,
                ProductHomeDeliveryInventory: productInventory || undefined
            };
        });

        return productsWithInventory;
    } catch (error) {
        context.telemetry.error('Error fetching product inventory', error);
        return products; // Return original products if there's an error
    }
}

/**
 * Reuses the current category hierarchy already fetched by the module's `categoryHierarchy` data action.
 * Calling the observable data action with a matching cache key resolves from the request cache, so no new API call is made.
 * @param context - The context.
 * @returns The current category hierarchy, or an empty array when unavailable.
 */
async function getCurrentCategoryHierarchyData(context: IActionContext): Promise<CategoryHierarchy[]> {
    try {
        const { urlTokens, query } = context.requestContext;
        const activeCategoryId = urlTokens.pageType === 'Category' ? urlTokens.itemId : query?.categoryId;
        const activeCategoryParsedId = activeCategoryId ? Number.parseInt(activeCategoryId, 10) : undefined;

        return await getCurrentCategoryHierarchy(
            new CurrentCategoryHierarchyInput(context.requestContext, true, undefined, activeCategoryParsedId),
            context
        );
    } catch (error) {
        context.telemetry.error('Error fetching category hierarchy', error);
        return [];
    }
}

/**
 * Action function to fetch products for a list page.
 * @param input - The input.
 * @param context - The context.
 * @returns IFullProductsSearchResultsWithCount - The full product search result with count.
 */
async function action(input: GetFullProductsByCollectionInput, context: IActionContext): Promise<IFullProductsSearchResultsWithCount> {
    let promise: AsyncResult<ProductSearchResultExtended[]>;
    let searchProductId;
    const searchCriteriaInput: ProductSearchCriteria = {};
    searchCriteriaInput.Context = {
        ChannelId: context.requestContext.apiSettings.channelId,
        CatalogId: context.requestContext.apiSettings.catalogId
    };
    searchCriteriaInput.Refinement = input.refiners;
    searchCriteriaInput.IncludeAttributes = input.includeAttributes;
    searchCriteriaInput.SkipVariantExpansion = true;

    const categoryHierarchy = await getCurrentCategoryHierarchyData(context);
    const allCategories = await getCategoryHierarchyAction(new CategoriesInput(context.requestContext, true, 1, true), context);
    const allCategory = allCategories[0];

    context.telemetry.information(`Category hierarchy depth: ${categoryHierarchy.length}`);

    const unbxdUid = context.requestContext.cookies.get('unbxd.userId').value as string;
    const defaultNumber: number = 0;

    const categoryIds = categoryHierarchy.map(category => category.RecordId).join('>');

    let language = unbxdUid;

    if (input.pageType === 'Category') {
        const languagePayload = {
            uid: unbxdUid,
            p: `${allCategory.RecordId}>${categoryIds}`
        };
        language = JSON.stringify(languagePayload);
    }

    searchCriteriaInput.Language = language;

    // VSI CUSTOMIZATION - START - pass uc_param to searchByCriteria if exist in url
    let uc_param = context.requestContext.query && context.requestContext.query.uc_param ? context.requestContext.query.uc_param : '';
    uc_param = uc_param.replace(/\s/g, '%20');
    // VSI CUSTOMIZATION - END -
    if (input.pageType === 'Category' || (context.requestContext.query && context.requestContext.query.categoryId)) {
        if (input.category) {
            searchCriteriaInput.CategoryIds = [input.category || defaultNumber];
            if (uc_param) {
                searchCriteriaInput.SearchCondition = `&uc_param=${uc_param}`;
            }
            promise = searchByCriteriaAsync(
                {
                    callerContext: context,
                    queryResultSettings: input.queryResultSettings
                },
                searchCriteriaInput
            );
        } else {
            throw new Error('[GetFullProductsForCollection]Category Page Detected, but no global categoryId found');
        }
    } else if (input.searchText && context.requestContext.query && context.requestContext.query.q) {
        searchCriteriaInput.SearchCondition = input.searchText;
        searchCriteriaInput.SearchCondition = uc_param ? `${input.searchText}&uc_param=${uc_param}` : input.searchText;
        promise = searchByCriteriaAsync(
            {
                callerContext: context,
                queryResultSettings: input.queryResultSettings
            },
            searchCriteriaInput
        );
    } else if (
        input.searchText &&
        context.requestContext.query &&
        context.requestContext.query.productId &&
        context.requestContext.query.recommendation
    ) {
        const searchObject = JSON.parse(input.searchText);
        searchProductId = Number(searchObject.ProductId);
        if (Number.isNaN(searchProductId)) {
            throw new Error('Failed to cast search product id into a number.');
        } else if (!searchObject.Recommendation) {
            throw new Error('Failed to retrieve the Recommendation.');
        } else {
            searchCriteriaInput.RecommendationListId = searchObject.Recommendation;
            searchCriteriaInput.Ids = [searchProductId || defaultNumber];
            if (uc_param) {
                searchCriteriaInput.SearchCondition = `&uc_param=${uc_param}`;
            }
            promise = searchByCriteriaAsync(
                {
                    callerContext: context,
                    queryResultSettings: input.queryResultSettings
                },
                searchCriteriaInput
            );
        }
    } else {
        throw new Error('[GetFullProductsForCollection]Search Page Detected, but no q= or productId= query parameter found');
    }

    const productSearchResults = await promise;
    // Update ListPageState For SSR
    if (!MsDyn365.isBrowser) {
        context.update(new ListPageStateInput(), {
            totalProductCount: promise.metadata.count ?? defaultNumber,
            activeFilters: input.refiners
        });
    }

    return returnProducts(productSearchResults, context, promise.metadata.count);
}

export const actionDataAction = createObservableDataAction({
    id: '@msdyn365-commerce-modules/search-result-container/get-full-products-by-collection',
    action: <IAction<IFullProductsSearchResultsWithCount>>action,
    input: createInput
});

export default actionDataAction;
